import { GetAudiologistsOfSupplierParams, useGetAudiologistsOfSupplier } from "@bvhi/backend-api";
import { Alert, Button, Card, Stack } from "@bvhi/ui-base";
import { AddIcon, ErrorIcon } from "@bvhi/ui-icons";
import { Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NavigationBar } from "../../../../../components/navigation-bar";
import { useAuthContext } from "../../../../../context/use-auth-context";
import { useInviteAudiologistContext } from "../../../../../context/use-invite-audiologist-context";
import { useBVHIDataGridFilter } from "../../../../../hooks/use-bvhi-datagrid-Filter";
import { useErrorObjectMapper } from "../../../../../hooks/use-error-object-mapper";
import { Route } from "../../../../../routes/_main/app/audiologists.index";
import { AudiologistsFilterCard } from "./components/audiologists-filter-card";
import { AudiologistsDataGrid } from "./components/datagrid/audiologists-datagrid";
import { defaultAudiologistFilter } from "./components/default-audiologist-filter";
import { useAudiologistsSortParams } from "./hooks/use-audiologists-sort-params";
import { DownloadConnectedAudiologistsCard } from "./components/download-connected-audiologists-card";

export const AudiologistsListView = () => {
  const searchParams = Route.useSearch();
  const { currentUser } = useAuthContext();
  const { t } = useTranslation(["toasts", "audiologists"]);
  const { setOpen: setAddAudiologistToSupplierDialogOpen } = useInviteAudiologistContext();
  const { getSortConfig } = useAudiologistsSortParams();
  const { getErrorMessagesFromErrorObject } = useErrorObjectMapper();
  const { resetFilter, handleFilterChange, handleDataGridChange, requestParams } = useBVHIDataGridFilter<GetAudiologistsOfSupplierParams>(
    defaultAudiologistFilter,
    Route.fullPath,
    getSortConfig,
    searchParams
  );

  const { error } = useGetAudiologistsOfSupplier(currentUser?.organisation?.id ?? "", requestParams);

  const errorMessages = useMemo(() => {
    if (!error?.errors) return [];
    const errItems = getErrorMessagesFromErrorObject(error);
    return errItems.map((errItem) => (
      <Alert key={errItem} icon={<ErrorIcon />} severity="error">
        {errItem}
      </Alert>
    ));
  }, [error, getErrorMessagesFromErrorObject]);

  return (
    <>
      <NavigationBar
        showBreadcrumbs={true}
        actions={[
          <Button key="1" onClick={() => setAddAudiologistToSupplierDialogOpen(true)} startIcon={<AddIcon />}>
            {t("audiologists:audiologists.invite")}
          </Button>,
        ]}
      />

      <Stack pt={2} spacing={2}>
        {errorMessages.map((errorComponent) => errorComponent)}

        <AudiologistsFilterCard filter={requestParams} onChange={handleFilterChange} onReset={resetFilter} />

        <Suspense fallback={<Card sx={{ height: 300, minHeight: 300 }} />}>
          <AudiologistsDataGrid requestParams={requestParams} updateCallback={handleDataGridChange} />
        </Suspense>
        <DownloadConnectedAudiologistsCard />
      </Stack>
    </>
  );
};
